import React, { useEffect, useRef, useState } from "react";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import { slugify } from "../../lib/utils";
import TableOfContents from "../../components/TableOfContents";
import SEO from "../../components/SEO";

// Structured blog article component with improved formatting & table of contents.
const IntroducingBoxiaTab = () => {
  const articleRef = useRef(null);
  const [headings, setHeadings] = useState([]);

  // Collect headings for table of contents.
  useEffect(() => {
    if (!articleRef.current) return;
    const selector = "h2, h3";
    const nodes = Array.from(articleRef.current.querySelectorAll(selector));
    const seen = new Map();
    const list = nodes.map((node) => {
      const base = slugify(node.textContent || "");
      let id = base;
      let i = 2;
      while (seen.has(id) || document.getElementById(id)) {
        id = `${base}-${i++}`;
      }
      seen.set(id, true);
      node.id = id;
      return {
        id,
        text: node.textContent,
        level: Number(node.tagName.replace("H", "")),
      };
    });
    setHeadings(list.filter((h) => h.level === 2)); // Only show h2 in TOC for clarity
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Introducing Boxia Tab – Ultra-Fast AI Autocomplete Everywhere"
        description="Boxia Tab is a lightning-fast, context-aware AI autocomplete system available across your entire productivity workspace—notes, tasks, calendar, docs & more."
        url={typeof window !== 'undefined' ? `${window.location.origin}/blog/introducing-boxia-tab` : 'https://boxia.io/blog/introducing-boxia-tab'}
        image={"https://boxia.io/og/introducing-boxia-tab.png"}
        type="article"
        publishedTime="2025-11-10"
        author="Radin Reth, Ian Murphy, Jake"
        tags={["Boxia Tab","AI autocomplete","productivity","workspace","typing speed","context aware"]}
      />
      <NavBar />
      <main
        className="flex-grow bg-white dark:bg-[#121212] transition-colors duration-300 relative"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 50% 0%, rgba(120,119,198,0.10), rgba(255,255,255,0)), radial-gradient(ellipse 60% 80% at 50% 0%, rgba(120,119,198,0.18), rgba(26,26,26,0))",
        }}
      >
        <section className="relative pt-[120px]">
          <div className="relative z-10 mx-auto max-w-6xl px-4 md:px-8 pb-28">
            <a
              href="/blog"
              className="inline-block mb-8 text-purple-600 dark:text-purple-300 hover:text-pink-500 dark:hover:text-orange-200 font-medium transition-colors"
            >
              ← Back to Blog
            </a>
            {/* Header / Hero */}
            <header className="mb-10 max-w-3xl mx-auto text-center">
              <h1 className="font-semibold tracking-tight text-4xl md:text-5xl text-gray-900 dark:text-white mb-4">
                Introducing Boxia Tab
              </h1>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-x-2 mb-4 flex justify-center">
                <span>By Radin, Ian, and Jake</span>
                <span>•</span>
                <time dateTime="2025-11-10">Nov 10, 2025</time>
              </div>
              <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                The fastest, smartest AI-powered code completion system ever
                built, designed to feel invisible until you need it.
              </p>
            </header>
            {/* Table of Contents */}
            <div className="max-w-3xl mx-auto">
              <TableOfContents headings={headings} />
            </div>
            {/* Article */}
            <article
              ref={articleRef}
              className="prose dark:prose-invert prose-lg max-w-3xl mx-auto"
            >
              <h2>What is Boxia Tab?</h2>
              <p>
                When you're writing emails, notes, documentation, or managing
                tasks, your flow matters. Context switching kills productivity.
                Waiting for suggestions breaks your rhythm. That's why we built{" "}
                <strong>Boxia Tab</strong>, an AI autocomplete system that
                predicts what you're about to type and delivers it faster than
                you can blink.
              </p>
              <p>
                Most autocomplete tools give you one thing or the other: either
                they're fast with terrible suggestions, or smart but laggy.
                Cursor Tab is excellent, fast and intelligent, but it only works
                in the Cursor IDE. We built Boxia Tab because we needed that
                same power everywhere: in emails, notes, issues, calendar
                events, and across your entire productivity workspace. It
                understands context, picks up on patterns, and streams
                suggestions before you've finished thinking them.
              </p>
              <h3>The Problem: Speed vs. Intelligence</h3>
              <p>
                Autocomplete has always forced developers into a frustrating
                choice: fast or smart, pick one. GitHub Copilot has delivered
                solid suggestions since Codex launched in 2023, but the
                200-500ms latency breaks your flow. They finally shipped their
                own model just two weeks ago to improve this, though they're
                still behind Cursor. Cursor and Supermaven nailed the
                speed-intelligence problem, giving you both instant responses
                and smart suggestions right in your IDE. Traditional
                autocomplete is fast but dumb. It has no idea what you're
                actually trying to build.
              </p>
              <p>
                We refused to accept this compromise. Boxia Tab delivers both
                lightning-fast responses and intelligent, context-aware
                suggestions everywhere you work.
              </p>
              <h2>How We Built It</h2>
              <h3>Our Tab Model</h3>
              <p>
                We tested dozens of models: GPT-4o Mini, Gemini Flash Lite,
                Llama 3.1 via Groq, and more. Most were either too slow, too
                expensive, or hallucinated too much, especially Llama. So we
                built our own <strong>Tab</strong> model ourselves, an in‑house
                distilled transformer tuned for ultra-fast autocomplete.
              </p>
              <p>
                Our Tab model is optimized for speed and accuracy, producing
                predictions in milliseconds while staying anchored to your
                context.
              </p>
              <h3>The Delivery</h3>
              <p>
                Autocomplete only feels instant if the first token shows up
                almost immediately. Boxia Tab streams completions over{" "}
                <strong>Server‑Sent Events (SSE)</strong>, a lean one‑way
                channel that skips the persistent heartbeat overhead of
                WebSockets. We open a single HTTP connection and then emit
                tokens as a continuous event stream with the first token in
                about <strong>&lt;25ms</strong>, with the remainder pipelined so
                you can accept part of a suggestion while the tail is still
                generating. If a network hiccup occurs we transparently retry
                with jittered backoff.
              </p>
              <p>
                We chose SSE for its simplicity, HTTP/2 multiplexing, and easy
                CDN handling of short bursts. When we need bi‑directional
                control (e.g. cancel), we send a lightweight abort request on a
                separate endpoint, keeping the stream itself pure and fast.
              </p>
              <h3>The Intelligence</h3>
              <p>
                Boxia is an all‑in‑one productivity platform, so Boxia Tab has
                to feel natural whether you're polishing lines of code, drafting
                an email, logging an issue, or jotting a note for next week’s
                planning. Instead of treating everything like plain text, it
                quietly reads the room: what kind of surface you're in, what
                you've written so far, and the patterns you personally lean
                toward.
              </p>
              <p>
                We keep it simple on the outside and purposeful under the hood.
                Tab:
              </p>
              <ul>
                <li>
                  <strong>Adapts to the surface:</strong> Code, markdown, or
                  plain text each get suggestions that match their style and
                  formatting.
                </li>
                <li>
                  <strong>Understands the task:</strong> A meeting note feels
                  different from a bug report. Suggestions reflect the intent of
                  where you're writing.
                </li>
                <li>
                  <strong>Learns from you:</strong> It picks up tone, naming
                  habits, and how verbose or concise you tend to be, and then
                  mirrors it.
                </li>
              </ul>
              <p>
                The result is prediction that feels less like a generic AI and
                more like a teammate finishing your thought everywhere inside
                Boxia.
              </p>
              <h3>The Memory</h3>
              <p>
                Generating completions from scratch every time would be
                wasteful. Instead, Boxia Tab uses a{" "}
                <strong>multi-layer semantic cache</strong> with LRU (Least
                Recently Used) eviction:
              </p>
              <ul>
                <li>
                  <strong>Exact match cache:</strong> If you've typed this exact
                  context before, we return the cached suggestion instantly.
                </li>
                <li>
                  <strong>Semantic similarity cache:</strong> Even if the text
                  differs slightly, we use similarity matching (0.6-0.8
                  threshold) to find relevant cached suggestions.
                </li>
                <li>
                  <strong>Rejected suggestion tracking:</strong> We remember
                  what you rejected and never show it again.
                </li>
                <li>
                  <strong>Cache validation:</strong> Quality filters ensure only
                  high-confidence suggestions are cached.
                </li>
              </ul>
              <h3>The Timing</h3>
              <p>
                We don't want to spam the API with every keystroke, but we also
                don't want to wait too long. Boxia Tab uses{" "}
                <strong>adaptive debouncing</strong> based on your typing speed:
              </p>
              <p>
                This means if you're blazing through code, suggestions appear
                almost instantly. If you pause to think, we give you space, then
                respond the moment you start typing again.
              </p>
              <h3>The Format: Multi-line Suggestions</h3>
              <p>
                Most autocompletes only suggest single lines. Boxia Tab supports
                up to{" "}
                <strong>10 lines of intelligent, formatted suggestions</strong>:
              </p>
              <ul>
                <li>
                  <strong>Smart indentation:</strong> Detects whether you use
                  spaces or tabs, and how many (2, 4, 8 spaces).
                </li>
                <li>
                  <strong>Line break intelligence:</strong> Suggests breaks at
                  sentence boundaries, list items, code blocks, etc.
                </li>
                <li>
                  <strong>Markdown awareness:</strong> Understands headings,
                  lists, quotes, code fences, and formats suggestions
                  accordingly.
                </li>
                <li>
                  <strong>Code block formatting:</strong> Properly indents
                  nested code structures (functions, classes, loops).
                </li>
              </ul>
              <h2>The Experience</h2>
              <p>
                Great autocomplete should feel like reading your mind. We
                achieve this through:
              </p>
              <h3>Visual Feedback</h3>
              <ul>
                <li>
                  <strong>Confidence-based UI:</strong> High-confidence
                  suggestions appear more solid, uncertain ones are more
                  transparent.
                </li>
                <li>
                  <strong>Color tinting:</strong> Green for high confidence,
                  purple for medium, yellow for lower. Subtle visual cues
                  without distraction.
                </li>
              </ul>
              <p>
                This helps you gauge how much to trust a suggestion at a glance,
                and all of this is changable in settings.
              </p>
              <h3>Flexible Acceptance</h3>
              <p>
                You're in control. Tab offers multiple ways to accept
                suggestions:
              </p>
              <ul>
                <li>
                  <kbd>Tab</kbd> - Accept the full suggestion
                </li>
                <li>
                  <kbd>→</kbd> - Accept the next character
                </li>
                <li>
                  <kbd>Ctrl + →</kbd> - Accept the next word
                </li>
                <li>
                  <kbd>Ctrl + ↵</kbd> - Accept to the next line
                </li>
                <li>
                  <kbd>Esc</kbd> - Reject the suggestion
                </li>
              </ul>
              <h3>Smart Validation</h3>
              <p>Not all suggestions are worth showing. We filter out:</p>
              <ul>
                <li>Repetitive suggestions (same text over and over)</li>
                <li>Overlapping text (suggesting what's already typed)</li>
                <li>
                  Previously rejected suggestions (it learns from your
                  rejections)
                </li>
                <li>Low-confidence hallucinations</li>
                <li>Only shows meaningful continuations that add value</li>
              </ul>
              <h2>Data & Privacy</h2>
              <p>
                Tab learns from how you interact with suggestions, but it does
                so with your privacy at the center. We collect a small set of{" "}
                <strong>privacy‑safe signals</strong> to help us tune speed,
                relevance, and future RLHF improvements, but never full document
                contents or private workspace data.
              </p>
              <p>What we capture:</p>
              <ul>
                <li>
                  <strong>Latency + performance:</strong> When a request starts
                  and when the first token arrives.
                </li>
                <li>
                  <strong>Suggestion lifecycle:</strong> Whether a suggestion
                  was shown, accepted fully, accepted partially (character /
                  word / line), ignored, or rejected.
                </li>
                <li>
                  <strong>Basic shape:</strong> Anonymous length, number of
                  lines, and a hashed fingerprint (for dedup + cache tuning).
                </li>
                <li>
                  <strong>Error + retry data:</strong> To spot instability and
                  fix it fast.
                </li>
              </ul>
              <p>
                What we do <em>not</em> store for training:
              </p>
              <ul>
                <li>Full email, note, issue, card, or calendar content.</li>
                <li>
                  Secrets like API keys, tokens, passwords. Automated scanners
                  drop obvious matches immediately.
                </li>
                <li>
                  Personally identifying text (names, emails) beyond transient
                  in‑memory processing.
                </li>
              </ul>
              <p>
                For RL training, we convert your interactions into lightweight
                reward signals: accept = positive, partial accept = scaled
                positive, reject = negative, ignore = neutral. These are
                aggregated and detached from your account before model tuning.
                Occasional sample snippets (truncated to a small window) may be
                briefly queued for automated quality checks, then purged. They
                never enter long‑term storage if they trip a redaction rule.
              </p>
              <p>
                You can toggle advanced telemetry in Settings at any time. Even
                with everything off, Tab still works, just with fewer
                personalized improvements. For details, see our{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
              <h2>Available Everywhere</h2>
              <p>
                Wherever you type inside Boxia, whether it be emails, notes,
                issues, cards, calendar events, todos, planning docs, and more,
                Boxia Tab is quietly active. No mode switching or separate
                install; the same fast, context‑aware suggestions follow you
                across the entire workspace so your flow never fractures.
              </p>

              <div className="mt-12 text-center">
                <h2>Try Boxia Tab</h2>
                <p className="max-w-xl mx-auto">
                  Boxia Tab is already live everywhere you type inside Boxia.
                  Start your <strong>14‑day free trial</strong> and feel
                  autocomplete that keeps pace with your ideas.
                </p>
                <p className="mt-6">
                  <a
                    href="https://app.boxia.io/auth/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Start your 14-day trial
                  </a>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                  No credit card required.
                </p>
              </div>
              <footer className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  Written by Radin, Ian, and Jake •{" "}
                  <time dateTime="2025-11-10">Nov 10, 2025</time>
                </p>
              </footer>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default IntroducingBoxiaTab;
